/* FalcX add-ons, behaviour (r8). Loaded as a module by snippets/falcx-addon-block.liquid (product pages) and
   sections/falcx-addon-cart.liquid (cart page). Needs assets/falcx-addon-core.js (inlined before it).
   - Add / Undo go through Shopify's standard cart action (Shopify.actions.updateCart). Horizon configures that action
     (assets/standard-actions-override.js) so its cart drawer, cart page and cart count update themselves from the
     shopify:cart:lines-update event it emits. Without it, the same event is dispatched here around /cart/add.js.
     No Horizon file is edited.
   - Cart drawer: Horizon's drawer is not editable, so the "Goes with your coilovers" strip is fetched with the Section
     Rendering API (?sections=falcx-addon-cart, same Liquid as the cart page) and placed at the end of the drawer's
     item list. Horizon re-renders the drawer after every cart change; the strip is put back when that happens.
   - Nothing is ever added without a click. No timers, no pressure wording. */
const FA = window.FalcxAddon;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const PHONE = '0449 887 291';
const SMS_HREF = 'sms:+61449887291'; // r10: the number in a message is a text link, never plain text (iPhone makes plain numbers call links)
const MSG = {
  added: 'Added to your cart.',
  removed: 'Removed from your cart.',
  soldout: (name) => `${name} is sold out right now. Text ${PHONE} if you need it.`,
  failed: `That didn't go through. Try again, or text ${PHONE}.`,
};

const gid = (id) => `gid://shopify/ProductVariant/${id}`;
async function cartJSON() {
  const r = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) throw new Error('cart');
  return r.json();
}
const qtyOf = (cart, id) => (cart.items || []).filter((i) => String(i.variant_id) === String(id)).reduce((n, i) => n + i.quantity, 0);
const lineOf = (cart, id) => (cart.items || []).find((i) => String(i.variant_id) === String(id)) || null;

/* Fallback when Shopify.actions is missing: Horizon's own event, as its product form dispatches it. */
async function viaEvent(target, action, lines, request) {
  const { CartLinesUpdateEvent } = await import('@shopify/events');
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({ action, context: target.closest('[data-fxa-mount="product"]') ? 'product' : 'cart', lines, promise: deferred.promise }));
  try {
    const res = await request();
    const body = await res.json().catch(() => ({}));
    const cart = await cartJSON();
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: !!body.status, source: 'falcx-addon' } });
    return body.status ? { error: body.description || body.message || 'error' } : {};
  } catch (e) {
    deferred.reject(e);
    throw e;
  }
}
async function setLines(target, action, lines, ajax) {
  const actions = window.Shopify && window.Shopify.actions;
  if (actions && typeof actions.updateCart === 'function') {
    const res = await actions.updateCart({ lines });
    const problem = (res && res.userErrors && res.userErrors[0]) || null;
    return problem ? { error: problem.message || 'error', code: problem.code } : {};
  }
  return viaEvent(target, action, lines, ajax);
}
async function addVariant(target, id) {
  const before = qtyOf(await cartJSON(), id);
  const res = await setLines(target, 'add', [{ merchandiseId: gid(id), quantity: 1 }], () =>
    fetch((window.Theme && Theme.routes && Theme.routes.cart_add_url) || `${root}cart/add.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    }));
  /* Trust the cart, not the call: a sold-out option comes back as an error or as a warning with nothing added. */
  const after = qtyOf(await cartJSON(), id);
  return { ok: after > before, error: res.error, code: res.code };
}
async function removeOne(target, id) {
  const line = lineOf(await cartJSON(), id);
  if (!line) return { ok: true };
  const quantity = Math.max(0, line.quantity - 1);
  const res = await setLines(target, quantity ? 'update' : 'remove', [{ id: line.key, quantity }], () =>
    fetch(`${root}cart/change.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity }),
    }));
  const after = lineOf(await cartJSON(), id);
  return { ok: !after || after.quantity <= quantity, error: res.error };
}

function parts(row) {
  return {
    select: row.querySelector('[data-fxa-select]'), add: row.querySelector('[data-fxa-add]'), done: row.querySelector('[data-fxa-done]'),
    undo: row.querySelector('[data-fxa-undo]'), msg: row.querySelector('[data-fxa-msg]'), price: row.querySelector('[data-fxa-price]'),
    name: (row.querySelector('.fxa-name a') || {}).textContent || 'This option',
  };
}
function say(p, text, isError) {
  if (!p.msg) return;
  const at = text.indexOf(PHONE);
  if (at < 0) {
    p.msg.textContent = text;
  } else {
    const a = document.createElement('a');
    a.href = SMS_HREF;
    a.textContent = PHONE;
    p.msg.replaceChildren(text.slice(0, at), a, text.slice(at + PHONE.length));
  }
  p.msg.classList.toggle('fxa-msg--error', !!isError);
}
function setAdded(row, added) {
  const p = parts(row);
  row.toggleAttribute('data-fxa-added', added);
  if (p.add) p.add.hidden = added;
  if (p.done) p.done.hidden = !added;
  if (p.undo) p.undo.hidden = !added;
  if (p.select && p.select.tagName === 'SELECT') p.select.disabled = added;
}
/* The row already names the product, so a sold-out option is named by itself ("12KG"); a one-option add-on by name. */
function optionName(p) {
  if (p.select && p.select.tagName === 'SELECT') {
    const o = p.select.options[p.select.selectedIndex];
    if (o && o.textContent) return o.textContent;
  }
  return p.name;
}
async function onAdd(btn) {
  const row = btn.closest('.fxa-row');
  const p = parts(row);
  if (btn.getAttribute('aria-disabled') === 'true' || !p.select) return;
  const id = p.select.value;
  btn.setAttribute('aria-disabled', 'true');
  btn.classList.add('is-busy');
  say(p, '');
  try {
    const res = await addVariant(btn, id);
    if (res.ok) {
      row.dataset.fxaVariant = id;
      setAdded(row, true);
      say(p, MSG.added);
      const mount = row.closest('[data-fxa-mount]');
      if (mount) mount.setAttribute('data-fxa-hold', '');
      if (p.undo) p.undo.focus({ preventScroll: true });
    } else {
      say(p, MSG.soldout(optionName(p)), true);
      if (p.select && p.select.tagName === 'SELECT') {
        const o = p.select.options[p.select.selectedIndex];
        if (o) o.setAttribute('data-soldout', '');
      }
      btn.setAttribute('data-fxa-soldout', '');
    }
  } catch (e) {
    say(p, MSG.failed, true);
  } finally {
    btn.classList.remove('is-busy');
    if (!btn.hasAttribute('data-fxa-soldout')) btn.removeAttribute('aria-disabled');
  }
}
async function onUndo(btn) {
  const row = btn.closest('.fxa-row');
  const p = parts(row);
  const id = row.dataset.fxaVariant;
  if (!id || btn.getAttribute('aria-disabled') === 'true') return;
  btn.setAttribute('aria-disabled', 'true');
  try {
    const res = await removeOne(btn, id);
    if (res.ok) {
      setAdded(row, false);
      delete row.dataset.fxaVariant;
      say(p, MSG.removed);
      const mount = row.closest('[data-fxa-mount]');
      if (mount && !mount.querySelector('[data-fxa-added]')) mount.removeAttribute('data-fxa-hold');
      if (p.add) p.add.focus({ preventScroll: true });
    } else {
      say(p, MSG.failed, true);
    }
  } catch (e) {
    say(p, MSG.failed, true);
  } finally {
    btn.removeAttribute('aria-disabled');
  }
}
function onSelect(sel) {
  const row = sel.closest('.fxa-row');
  const p = parts(row);
  const o = sel.options[sel.selectedIndex];
  if (p.price && o) p.price.textContent = o.dataset.price || '';
  /* An option that already came back sold out stays unavailable; any other option can be tried. */
  const sold = !!(o && o.hasAttribute('data-soldout'));
  if (p.add) {
    p.add.toggleAttribute('data-fxa-soldout', sold);
    if (sold) p.add.setAttribute('aria-disabled', 'true'); else p.add.removeAttribute('aria-disabled');
  }
  say(p, sold ? MSG.soldout(optionName(p)) : '', sold);
}

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const add = t.closest('[data-fxa-add]');
  if (add) { e.preventDefault(); onAdd(add); return; }
  const undo = t.closest('[data-fxa-undo]');
  if (undo) { e.preventDefault(); onUndo(undo); }
});
document.addEventListener('change', (e) => {
  if (e.target instanceof HTMLSelectElement && e.target.matches('[data-fxa-select]')) onSelect(e.target);
});

/* ---- Cart data (Section Rendering API) ---- */
let pending = null;
async function cartData() {
  if (!pending) {
    pending = fetch(`${root}?sections=falcx-addon-cart`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((json) => {
        const html = json && json['falcx-addon-cart'];
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('script[data-fxa-data]');
        return node ? JSON.parse(node.textContent) : null;
      })
      .finally(() => { setTimeout(() => { pending = null; }, 0); });
  }
  return pending;
}

/* ---- Cart drawer strip ---- */
const drawer = () => document.querySelector('theme-drawer#cart-drawer');
let drawerMount = null;
function drawerTarget() {
  const d = drawer();
  return d ? d.querySelector('.cart-drawer__items') : null;
}
function placeDrawerMount() {
  if (!drawerMount || drawerMount.hidden) return;
  const target = drawerTarget();
  if (target && drawerMount.parentNode !== target) target.appendChild(drawerMount);
}
async function refreshDrawer(data) {
  if (!drawer()) return;
  if (drawerMount && drawerMount.hasAttribute('data-fxa-hold')) { placeDrawerMount(); return; }
  if (data === undefined) data = await cartData().catch(() => null);
  if (!drawerMount) {
    drawerMount = document.createElement('div');
    drawerMount.className = 'fxa-mount fxa-mount--drawer';
    drawerMount.id = 'fxa-drawer';
    drawerMount.setAttribute('data-fxa-mount', 'drawer');
    drawerMount.setAttribute('data-fxa-tag', 'h3');
    drawerMount.setAttribute('data-fxa-title', 'Goes with your coilovers');
    drawerMount.setAttribute('data-fxa-done', '');
    drawerMount.hidden = true;
  }
  FA.render(drawerMount, data, window);
  if (drawerMount.hidden) { if (drawerMount.parentNode) drawerMount.parentNode.removeChild(drawerMount); return; }
  placeDrawerMount();
}
function watchDrawer() {
  const d = drawer();
  if (!d) return;
  let wasOpen = false;
  new MutationObserver(() => {
    const open = d.hasAttribute('open') || !!d.querySelector('dialog[open]');
    if (open && !wasOpen) refreshDrawer();
    if (!open && wasOpen && drawerMount) { drawerMount.removeAttribute('data-fxa-hold'); }
    wasOpen = open;
    if (drawerMount && !drawerMount.hidden && !drawerMount.isConnected) placeDrawerMount();
  }).observe(d, { attributes: true, childList: true, subtree: true, attributeFilter: ['open'] });
}

/* ---- Cart page section ---- */
async function refreshCartMounts(data) {
  const mounts = document.querySelectorAll('[data-fxa-mount="cart"]:not([data-fxa-hold])');
  if (!mounts.length) return;
  if (data === undefined) data = await cartData().catch(() => null);
  mounts.forEach((m) => FA.render(m, data, window));
}

/* Any cart change (ours, Horizon's product form, the cart page, an app): re-plan once it has finished. */
document.addEventListener('shopify:cart:lines-update', (event) => {
  const done = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
  done.then(() => new Promise((r) => setTimeout(r, 60))).then(async () => {
    const data = await cartData().catch(() => null);
    refreshCartMounts(data);
    refreshDrawer(data);
  }).catch(() => {});
});
/* A different Falcon chosen: re-plan the visible mounts (except rows just added). */
document.addEventListener('falcx:vehicle-change', () => {
  document.querySelectorAll('[data-fxa-mount]:not([data-fxa-hold])').forEach((m) => {
    if (m.__fxaData !== undefined) FA.render(m, m.__fxaData, window);
  });
});

/* Horizon re-renders parts of the product page when the option changes; put the rows back if that wiped them. */
let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => { queued = false; if (document.querySelector('[data-fxa-mount]:not([data-fxa-done])')) FA.mountAll(document); });
}).observe(document.body, { childList: true, subtree: true });

if (FA) {
  FA.mountAll(document);
  /* The finder may name a different Falcon than the before-paint guess (e.g. a hub link); follow it. */
  document.querySelectorAll('[data-fxa-mount]:not([data-fxa-hold])').forEach((m) => {
    const v = FA.readVehicle(window);
    if (m.__fxaData !== undefined && (v ? v.series + '/' + v.body : '') !== m.__fxaVehicle) FA.render(m, m.__fxaData, window);
  });
  watchDrawer();
  /* Debug/test hook: render the drawer strip from given cart data (used to check the strip with a scripted cart). */
  FA.renderDrawer = (data) => refreshDrawer(data);
}